import React from 'react';
import { Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import { Page4ChildRow, Page4ParentRow } from './types';
import { wsrColors, wsrMonoFont } from './wsrTokens';

interface TicketTableProps {
  rows: Page4ParentRow[];
  onExportCsv?: () => void;
}

interface FlatRow {
  service: string;
  priority?: boolean;
  isParentRow: boolean;
  isTotalRow?: boolean;
  raised_nissan: number | null;
  raised_conexio: number | null;
  raised: number;
  resolved: number;
  outstanding: number;
  d0_7: number | null;
  d8_14: number | null;
  d14: number | null;
  total: number | null;
}

interface ColumnSpec {
  key: keyof FlatRow;
  label: string;
  align: 'left' | 'right';
  emphasize?: boolean;
  warn?: boolean;
}

const COLUMNS: ColumnSpec[] = [
  { key: 'service', label: 'Service', align: 'left' },
  { key: 'raised_nissan', label: 'Raised (Nissan)', align: 'right' },
  { key: 'raised_conexio', label: 'Raised (Conexio)', align: 'right' },
  { key: 'raised', label: 'Raised', align: 'right', emphasize: true },
  { key: 'resolved', label: 'Resolved', align: 'right', emphasize: true },
  { key: 'outstanding', label: 'Outstanding', align: 'right', emphasize: true },
  { key: 'd0_7', label: '0–7d', align: 'right' },
  { key: 'd8_14', label: '8–14d', align: 'right' },
  { key: 'd14', label: '>14d', align: 'right', warn: true },
  { key: 'total', label: 'Total', align: 'right', emphasize: true },
];

const PRIORITY_PILL: Record<string, { bg: string; fg: string }> = {
  P1: { bg: wsrColors.redPillBg, fg: wsrColors.redText },
  P2: { bg: wsrColors.orange50, fg: wsrColors.orangeText },
  P3: { bg: wsrColors.amberPillBg, fg: wsrColors.amberText },
  P4: { bg: wsrColors.bluePillBg, fg: wsrColors.blue700 },
};

const formatCell = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : String(value);

const flatten = (rows: Page4ParentRow[]): FlatRow[] => {
  const flat: FlatRow[] = [];
  rows.forEach((parent) => {
    flat.push({ ...parent, isParentRow: true });
    parent.children.forEach((child: Page4ChildRow) => flat.push({ ...child, isParentRow: false }));
  });
  flat.push(buildTotalRow(rows));
  return flat;
};

const buildTotalRow = (rows: Page4ParentRow[]): FlatRow => ({
  service: 'Total',
  isParentRow: false,
  isTotalRow: true,
  raised_nissan: rows.reduce((sum, r) => sum + r.raised_nissan, 0),
  raised_conexio: rows.reduce((sum, r) => sum + r.raised_conexio, 0),
  raised: rows.reduce((sum, r) => sum + r.raised, 0),
  resolved: rows.reduce((sum, r) => sum + r.resolved, 0),
  outstanding: rows.reduce((sum, r) => sum + r.outstanding, 0),
  d0_7: rows.reduce((sum, r) => sum + r.d0_7, 0),
  d8_14: rows.reduce((sum, r) => sum + r.d8_14, 0),
  d14: rows.reduce((sum, r) => sum + r.d14, 0),
  total: rows.reduce((sum, r) => sum + r.total, 0),
});

const ServiceCell: React.FC<{ row: FlatRow }> = ({ row }) => {
  if (row.priority) {
    const pill = PRIORITY_PILL[row.service] ?? { bg: wsrColors.slate100, fg: wsrColors.slate600 };
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          paddingLeft: '20px',
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '999px',
            background: pill.bg,
            color: pill.fg,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '.04em',
          }}
        >
          {row.service}
        </Box>
      </Box>
    );
  }

  if (row.isTotalRow) {
    return (
      <Box sx={{ fontWeight: 700, color: wsrColors.slate900 }}>{row.service}</Box>
    );
  }

  const dotColor = row.service === 'Incidents' ? wsrColors.red : wsrColors.blue;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <Box
        component="span"
        sx={{ width: 8, height: 8, borderRadius: '2px', background: dotColor }}
      />
      <Box component="span">{row.service}</Box>
    </Box>
  );
};

const TicketTable: React.FC<TicketTableProps> = ({ rows, onExportCsv }) => {
  const flatRows = flatten(rows);

  return (
    <Box
      sx={{
        background: '#fff',
        border: `1px solid ${wsrColors.slate200}`,
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${wsrColors.slate200}`,
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <Box>
          <Box sx={{ fontSize: 15, fontWeight: 600 }}>
            Ticket Analysis — NNA Conexio Support
          </Box>
          <Box sx={{ fontSize: 12, color: wsrColors.slate500, marginTop: '2px' }}>
            Raised, resolved &amp; outstanding by service and priority
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Box
            component="button"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 12px',
              fontSize: 12,
              fontWeight: 500,
              background: '#fff',
              border: `1px solid ${wsrColors.slate200}`,
              borderRadius: '6px',
              color: wsrColors.slate700,
              cursor: 'pointer',
              fontFamily: 'inherit',
              '&:hover': { background: wsrColors.slate50 },
            }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
            Add column
          </Box>
          <Box
            component="button"
            onClick={onExportCsv}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '7px 12px',
              fontSize: 12,
              fontWeight: 600,
              background: '#fff',
              border: `1px solid ${wsrColors.blue}`,
              borderRadius: '6px',
              color: wsrColors.blue,
              cursor: 'pointer',
              fontFamily: 'inherit',
              '&:hover': { background: wsrColors.blue50 },
            }}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />
            Export CSV
          </Box>
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 13,
          }}
        >
          <Box component="thead">
            <Box component="tr" sx={{ background: wsrColors.slate50 }}>
              {COLUMNS.map((col) => (
                <Box
                  key={col.key}
                  component="th"
                  sx={{
                    padding: '10px 14px',
                    textAlign: col.align,
                    fontSize: 11,
                    fontWeight: 600,
                    color: col.warn ? wsrColors.red : wsrColors.slate500,
                    textTransform: 'uppercase',
                    letterSpacing: '.06em',
                    borderBottom: `1px solid ${wsrColors.slate200}`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {col.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {flatRows.map((row, idx) => {
              const isParent = row.isParentRow;
              const isTotal = row.isTotalRow === true;
              return (
                <Box
                  key={`${row.service}-${idx}`}
                  component="tr"
                  sx={{
                    background: isTotal
                      ? wsrColors.slate100
                      : isParent
                        ? '#fff'
                        : wsrColors.slate50,
                    borderTop: isTotal
                      ? `2px solid ${wsrColors.slate300}`
                      : isParent && idx > 0
                        ? `1px solid ${wsrColors.slate200}`
                        : `1px solid ${wsrColors.slate100}`,
                  }}
                >
                  {COLUMNS.map((col) => {
                    const value = row[col.key] as number | string | null | undefined;
                    const isService = col.key === 'service';
                    const warnHighlight =
                      col.warn && typeof value === 'number' && value > 0;

                    return (
                      <Box
                        key={col.key}
                        component="td"
                        sx={{
                          padding: isTotal || isParent ? '12px 14px' : '8px 14px',
                          textAlign: col.align,
                          fontWeight: isTotal ? 700 : isParent && col.emphasize ? 700 : isParent ? 500 : 400,
                          color: warnHighlight
                            ? wsrColors.red
                            : isTotal || isParent
                              ? wsrColors.slate900
                              : wsrColors.slate600,
                          fontFamily: isService ? 'inherit' : wsrMonoFont,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isService ? (
                          <ServiceCell row={row} />
                        ) : (
                          formatCell(value as number | null | undefined)
                        )}
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TicketTable;
